import { ZoomIn, ZoomOut, Maximize, RotateCcw, Grid3x3 as Grid3X3, Magnet, Ruler, Home } from 'lucide-react';
import { useViewportStore, ZOOM_MIN, ZOOM_MAX } from '../../stores/viewportStore';
import { useCanvasStore } from '../../stores/canvasStore';

export default function ZoomControls() {
  const {
    zoom,
    zoomIn,
    zoomOut,
    fitToScreen,
    centerAtZoom,
    homeToggle,
    gridVisible,
    toggleGrid,
    snapEnabled,
    toggleSnap,
    smartGuidesEnabled,
    toggleSmartGuides,
  } = useViewportStore();

  const { canvasWidth, canvasHeight, fabricCanvas } = useCanvasStore();

  const zoomPercent = Math.round(zoom * 100);

  const getCanvasEl = () =>
    (fabricCanvas as any)?.lowerCanvasEl as HTMLCanvasElement | undefined;

  const handleFitToScreen = () => {
    const el = getCanvasEl();
    if (el) {
      fitToScreen(canvasWidth, canvasHeight, el.clientWidth, el.clientHeight);
    }
  };

  const handleZoomReset = () => {
    const el = getCanvasEl();
    if (el) {
      centerAtZoom(canvasWidth, canvasHeight, el.clientWidth, el.clientHeight, 1.0);
    }
  };

  const handleHome = () => {
    const el = getCanvasEl();
    if (el) {
      homeToggle(canvasWidth, canvasHeight, el.clientWidth, el.clientHeight);
    }
  };

  return (
    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 bg-zinc-900/95 backdrop-blur-sm border border-white/10 rounded-lg px-2 py-1.5 shadow-lg">
      <button
        onClick={handleHome}
        className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white transition-colors"
        title="Center on artboard (Shift+H)"
      >
        <Home size={16} />
      </button>

      <div className="w-px h-5 bg-white/10 mx-1" />

      <button
        onClick={zoomOut}
        disabled={zoom <= ZOOM_MIN}
        className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        title="Zoom Out (Ctrl+-)"
      >
        <ZoomOut size={16} />
      </button>

      <button
        onClick={handleZoomReset}
        className="text-xs text-zinc-300 font-medium w-14 text-center tabular-nums select-none rounded hover:bg-white/10 py-1 transition-colors"
        title="Reset to 100% (Ctrl+0)"
      >
        {zoomPercent}%
      </button>

      <button
        onClick={zoomIn}
        disabled={zoom >= ZOOM_MAX}
        className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        title="Zoom In (Ctrl+=)"
      >
        <ZoomIn size={16} />
      </button>

      <div className="w-px h-5 bg-white/10 mx-1" />

      <button
        onClick={handleFitToScreen}
        className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white transition-colors"
        title="Fit to Screen (Ctrl+1)"
      >
        <Maximize size={16} />
      </button>

      <button
        onClick={handleZoomReset}
        className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white transition-colors"
        title="Reset Zoom (Ctrl+0)"
      >
        <RotateCcw size={16} />
      </button>

      <div className="w-px h-5 bg-white/10 mx-1" />

      <button
        onClick={toggleGrid}
        className={`p-1.5 rounded transition-colors ${
          gridVisible
            ? 'bg-cyan-500/20 text-cyan-400'
            : 'text-zinc-400 hover:bg-white/10 hover:text-white'
        }`}
        title={`Grid ${gridVisible ? 'On' : 'Off'} (G)`}
      >
        <Grid3X3 size={16} />
      </button>

      <button
        onClick={toggleSnap}
        className={`p-1.5 rounded transition-colors ${
          snapEnabled
            ? 'bg-cyan-500/20 text-cyan-400'
            : 'text-zinc-400 hover:bg-white/10 hover:text-white'
        }`}
        title={`Snap ${snapEnabled ? 'On' : 'Off'}`}
      >
        <Magnet size={16} />
      </button>

      <button
        onClick={toggleSmartGuides}
        className={`p-1.5 rounded transition-colors ${
          smartGuidesEnabled
            ? 'bg-cyan-500/20 text-cyan-400'
            : 'text-zinc-400 hover:bg-white/10 hover:text-white'
        }`}
        title={`Smart Guides ${smartGuidesEnabled ? 'On' : 'Off'}`}
      >
        <Ruler size={16} />
      </button>
    </div>
  );
}
